<template>
  <div class="flex gap-4">
    <template v-if="mode === 'view'">
      <div>
        <ViewModeLabel>Email Template</ViewModeLabel>
        <ViewModeValue>{{ selectedEmailOption }}</ViewModeValue>
      </div>
    </template>
    <template v-else>
      <FormKit
        v-model="selectedEmailOption"
        label="Email Template"
        name="email_template"
        type="select"
        :options="emailTemplates.map(x => x.name)"
        validation="required"
        outer-class="!m-0 width-md"
      />
    </template>
    <UserSelector
      v-model:selected-type="recipientType"
      v-model:selected-user-ids="recipientUserIds"
      label="Recipient"
      name="recipient_type"
      :type-options="recipientTypeOptions"
      show-users-for-type="account_users"
      :mode="mode"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import type { ActionableProps } from '../types';
import type {
  WorkflowActionRecipientType,
  WorkflowActionableEmailParams,
  WorkflowActionEmailEntity,
} from '@/api/entity_types/workflow/WorkflowAction';
import { Api } from '@/api';
import type { EmailTemplateEntity } from '@/api/entity_types/EmailTemplate';
import ViewModeLabel from '../../common/ViewModeLabel.vue';
import ViewModeValue from '../../common/ViewModeValue.vue';
import UserSelector from './common/UserSelector.vue';

const { actionable: emailActionable, mode } = defineProps<ActionableProps<WorkflowActionEmailEntity>>();

const emailTemplates = ref<EmailTemplateEntity[]>([]);
const selectedEmailOption = ref<string>('');
const recipientType = ref<WorkflowActionRecipientType>('candidate');
const recipientUserIds = ref<number[]>([]);

const recipientTypeOptions = [
  { label: 'Candidate', value: 'candidate' },
  { label: 'Account Users', value: 'account_users' },
];

const initSelectedOption = (emailActionable?: WorkflowActionEmailEntity) => {
  const matchingEmailTemplate = emailTemplates.value.find(x => x.id === emailActionable?.email_template_id);
  if (matchingEmailTemplate) selectedEmailOption.value = matchingEmailTemplate.name;
  else selectedEmailOption.value = emailTemplates.value[0].name;

  recipientType.value = emailActionable?.recipient_type || 'candidate';
  recipientUserIds.value = emailActionable?.recipient_user_ids || [];
};

const buildActionableParams = () => {
  const selectedEmailTemplate = emailTemplates.value.find(x => x.name === selectedEmailOption.value);
  if (!selectedEmailTemplate) return;
  const params: WorkflowActionableEmailParams = {
    actionable_type: 'WorkflowActionEmail',
    actionable: {
      email_template_id: selectedEmailTemplate.id,
      recipient_type: recipientType.value,
      recipient_user_ids: recipientUserIds.value,
    },
  };
  return params;
};

const resetFields = (emailActionable: WorkflowActionEmailEntity) => {
  initSelectedOption(emailActionable);
};

defineExpose({
  buildActionableParams,
  resetFields,
});

onMounted(async () => {
  const response = await Api.EmailTemplates.list();
  emailTemplates.value = response.records;
  initSelectedOption(emailActionable);
});
</script>

<style scoped>
.width-md {
  @apply w-[18rem] max-w-[18rem];
}
</style>
